import Immutable from 'immutable';

import type ErrorData from 'sharedreact/data/ErrorData';
import Filter from 'sharedreact/data/Filter';
import type GroupAndCount from 'sharedreact/data/GroupAndCount';
import SortOrder from 'sharedreact/data/SortOrder';
import User from 'sharedreact/data/User';
import UserWithPassword from 'sharedreact/data/UserWithPassword';
import AppDispatcher from 'sharedreact/dispatcher/AppDispatcher';
import type { RangeType } from 'sharedreact/stores/generic/Type';

import UsersConnection from 'workspaces/common/connections/UsersConnection';
import UsersConstants from 'workspaces/common/constants/UsersConstants';

// private functions
function fetchSuccess (
  range: RangeType,
  totalCount: number,
  items: Immutable.List<User>,
  countPerGroup: Immutable.List<GroupAndCount>,
  filter: Filter,
  advancedFilter: Filter,
  useOnlyNamesDefaultFilter: boolean | null,
  groupBy: SortOrder,
  sortOrder: SortOrder
): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.GET_SUCCESS,
    range: range,
    totalCount: totalCount,
    items: items,
    countPerGroup: countPerGroup,
    filter: filter,
    advancedFilter: advancedFilter,
    useOnlyNamesDefaultFilter: useOnlyNamesDefaultFilter,
    groupBy: groupBy,
    sortOrder: sortOrder,
    logLevel: 'debug'
  });
}

function fetchFailure (range: RangeType, error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.GET_FAIL,
    range: range,
    error: error,
    logLevel: 'error'
  });
}

function fetchForOpenSuccess (id: string, items: Immutable.List<User>): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.GET_SUCCESS,
    items: items,
    logLevel: 'debug'
  });

  const userForId: User | undefined = items.find((user: User) => user.id === id);
  if (userForId != null) {
    AppDispatcher.dispatch({
      actionType: UsersConstants.GET_FOR_OPEN_SUCCESS,
      item: userForId,
      logLevel: 'trace'
    });
  } else {
    AppDispatcher.dispatch({
      actionType: UsersConstants.GET_FOR_OPEN_FAIL,
      id: id,
      logLevel: 'warn'
    });
  }
}

function fetchForOpenFailure (id: string, error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.GET_FOR_OPEN_FAIL,
    id: id,
    error: error,
    logLevel: 'error'
  });
}

function saveSuccess (item: User): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.SAVE_SUCCESS,
    item: item,
    logLevel: 'debug'
  });
}

function saveFailure (item: User | null, error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.SAVE_FAIL,
    item: item,
    error: error,
    logLevel: 'error'
  });
}

function removeSuccess (ids: Immutable.Set<string>): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.REMOVE_SUCCESS,
    ids: ids,
    logLevel: 'debug'
  });
}

function removeFailure (ids: Immutable.Set<string>, error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.REMOVE_FAIL,
    ids: ids,
    error: error,
    logLevel: 'error'
  });
}

// exported functions
function fetch (
  range: RangeType,
  filter: Filter,
  advancedFilter: Filter,
  useOnlyNamesDefaultFilter: boolean | null,
  groupBy: SortOrder,
  sortOrder: SortOrder
): void {
  UsersConnection.fetch(
    range,
    filter,
    advancedFilter,
    useOnlyNamesDefaultFilter,
    groupBy,
    sortOrder,
    fetchSuccess,
    fetchFailure
    );
}

function fetchForOpen (id: string): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.GET_FOR_OPEN,
    id: id,
    logLevel: 'trace'
  });

  UsersConnection.getById(
    [id],
    items => fetchForOpenSuccess(id, items),
    (ids: string[], error: ErrorData) => fetchForOpenFailure(ids[0], error)
  );
}

function _findSuccess (indexes: Immutable.Map<string, number>): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.FIND_SUCCESS,
    indexes: indexes,
    logLevel: 'debug'
  });
}

function _findFailure (ids: Immutable.Set<string>, error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.FIND_FAIL,
    ids: ids,
    error: error,
    logLevel: 'error'
  });
}

function find (
  ids: Immutable.Set<string>,
  filter: Filter,
  advancedFilter: Filter,
  useOnlyNamesDefaultFilter: boolean,
  groupBy: SortOrder,
  sortOrder: SortOrder
): void {
  UsersConnection.find(
    ids,
    filter,
    advancedFilter,
    useOnlyNamesDefaultFilter,
    groupBy,
    sortOrder,
    _findSuccess,
    _findFailure
  );
}

function _getByIdSuccess (items: Immutable.List<User>): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.GET_BY_ID_SUCCESS,
    items: items,
    logLevel: 'debug'
  });
}

function _getByIdFailure (ids: string[], error: ErrorData): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.GET_BY_ID_FAIL,
    ids: ids,
    error: error,
    logLevel: 'error'
  });
}

function getById (ids: string[]): void {
  UsersConnection.getById(ids, _getByIdSuccess, _getByIdFailure);
}

function reload (): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.RELOAD,
    logLevel: 'trace'
  });
}

function refresh (): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.REFRESH,
    logLevel: 'trace'
  });
}

function setSelection (selection: Immutable.Set<string>): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.SET_SELECTION,
    selection: selection,
    logLevel: 'trace'
  });
}

function modifySelection (
  id: string,
  modifiers: any = { ctrlKey: false, shiftKey: false }
): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.MODIFY_SELECTION,
    id: id,
    ctrlKey: modifiers.ctrlKey,
    shiftKey: modifiers.shiftKey,
    logLevel: 'trace'
  });
}

function create (): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.CREATE,
    logLevel: 'trace'
  });
}

function open (item: User, saved?: boolean): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.OPEN,
    item: item,
    saved: saved,
    logLevel: 'trace'
  });
}

function setValue (name: string, value: string | User): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.SET_VALUE,
    name: name,
    value: value,
    logLevel: 'trace'
  });
}

function save (
  item: UserWithPassword,
  onSuccess?: ((user: User) => void),
  onFailure?: ((user: User) => void)
): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.SAVE,
    item: item,
    logLevel: 'trace'
  });

  UsersConnection.save(
    item,
    (user: User) => {
      saveSuccess(user);
      if (onSuccess != null) onSuccess(user);
    },
    (user: User, error: ErrorData) => {
      saveFailure(user, error);
      if (onFailure != null) onFailure(user);
    }
  );
}

function allowMultipleSelections (allow: boolean) {
  AppDispatcher.dispatch({
    actionType: UsersConstants.ALLOW_MULTI_SELECT,
    allow: allow,
    logLevel: 'trace'
  });
}

function activateFilter (filterMode: string) {
  AppDispatcher.dispatch({
    actionType: UsersConstants.ACTIVATE_FILTER,
    filterMode: filterMode,
    logLevel: 'trace'
  });
}

function setFilter (filterMode: string, filter: Filter, filterString: string, useOnlyNamesDefaultFilter?: boolean): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.SET_FILTER,
    filterMode: filterMode,
    filter: filter,
    filterString: filterString,
    useOnlyNamesDefaultFilter: useOnlyNamesDefaultFilter,
    logLevel: 'trace'
  });
}

function setSortOrder (sortOrder: SortOrder): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.SET_SORTORDER,
    sortOrder: sortOrder,
    logLevel: 'trace'
  });
}

function remove (ids: Immutable.Set<string>): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.REMOVE,
    ids: ids,
    logLevel: 'trace'
  });

  UsersConnection.remove(ids, removeSuccess, removeFailure);
}

function ensureSelectionIsVisible (ensure: boolean): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.ENSURE_SELECTION_IS_VISIBLE,
    ensureSelectionIsVisible: ensure,
    logLevel: 'trace'
  });
}

function setVisibleRange (range: RangeType): void {
  AppDispatcher.dispatch({
    actionType: UsersConstants.SET_VISIBLE_RANGE,
    range: range,
    logLevel: 'trace'
  });
}

export default {
  fetch,
  fetchForOpen,
  find,
  getById,
  reload,
  refresh,
  setSelection,
  allowMultipleSelections,
  modifySelection,
  create,
  open,
  setValue,
  save,
  saveFailure,
  activateFilter,
  setFilter,
  setSortOrder,
  remove,
  ensureSelectionIsVisible,
  setVisibleRange
};
